import * as React from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import { HtmlAnchor } from '@helpcenter/analytics-components/src/html-anchor';
import { ContactUsSection as CsmContactUsSection } from '@helpcenter/contact-us-section';
import {
	isCsmContactUsSectionEnabled,
	isFooterInlineSvgIconEnabled,
} from '@helpcenter/feature-flags';
import * as fonts from '@helpcenter/styles/src/fonts';
import * as transitions from '@helpcenter/styles/src/transitions';
import JsdIcon from 'assets/images/jsd-icon.react.svg';
import jsdIconSrc from 'assets/images/jsd-icon.svg';
import { FormattedMessage } from 'react-intl';
import { di } from 'react-magnetic-di';
import { useRouter } from 'react-resource-router';
import { AnalyticsContext } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';
import { isCSMHelpCenter } from '@atlassian/help-center-common-util/advanced-help-center';
import { getEnv } from '@atlassian/help-center-common-util/env';
import i18n from './messages';
import { footerPadding, footerJsdIconHeight } from './styled';

export interface Props {
	linkTo: string;
	className?: string;
	isCSM?: boolean;
}

const EDIT_HOMEPAGE_ROUTE_NAME = 'edit-homepage';

const Footer = ({ linkTo, className, isCSM }: Props) => {
	di(useRouter);
	const [{ route }] = useRouter();

	// isCSM check is used for generating correct footer in CSMRequest Type Previews
	const showCSMFooter = isCSMHelpCenter(getEnv().helpCenterType) || isCSM;

	const renderCsmFooter = () => {
		// TODO: move contact us section to edit csm page, once page builder allows additional children components
		return isCsmContactUsSectionEnabled() && route.name === EDIT_HOMEPAGE_ROUTE_NAME ? (
			<CsmContactUsSection />
		) : null;
	};

	return (
		<AnalyticsContext data={{ source: 'footer' }}>
			<FooterContainer
				id="portal-layout-footer"
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Disabled to rollout go/ui-styling-standard tooling, please resolve
				className={className}
				data-testid="portal-layout-footer"
			>
				{showCSMFooter ? (
					renderCsmFooter()
				) : (
					<FooterLink
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Disabled to rollout go/ui-styling-standard tooling, please resolve
						className="cv-exclude-from-customisation"
						target="_blank"
						rel="noopener noreferrer"
						href={linkTo}
						actionSubjectId="waclink"
					>
						<FormattedMessage
							{...i18n.footer}
							values={{
								iconHtml: isFooterInlineSvgIconEnabled() ? (
									<FooterJsdIcon />
								) : (
									<FooterJsdIconImg src={jsdIconSrc} alt="" />
								),
							}}
						/>
					</FooterLink>
				)}
			</FooterContainer>
		</AnalyticsContext>
	);
};

export default Footer;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const FooterContainer = styled.footer`
	padding: ${token('space.600', `${footerPadding}px`)} 0;
	text-align: center;
	white-space: nowrap;
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const FooterJsdIconImg = styled.img`
	object-fit: contain;
	height: ${token('space.250', `${footerJsdIconHeight}px`)};
	vertical-align: middle;
	opacity: 0.5;
	transition: opacity ${transitions.timingFunctions.easeInOut} ${transitions.speedMs.fast}ms;
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const FooterJsdIcon = styled(JsdIcon)`
	object-fit: contain;
	height: ${token('space.250', `${footerJsdIconHeight}px`)};
	vertical-align: middle;
	opacity: 0.5;
	transition: opacity ${transitions.timingFunctions.easeInOut} ${transitions.speedMs.fast}ms;
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const FooterLink = styled(HtmlAnchor)`
	${fonts.small};
	color: ${token('color.text.subtlest', '#5E6C84')};
	cursor: pointer;

	:hover,
	:active {
		color: ${token('color.text.subtlest', '#5E6C84')};
		text-decoration: none;

		${FooterJsdIcon} {
			opacity: 1;

			stop:first-child {
				stop-color: ${token('color.background.brand.bold.hovered', '#0052CC')};
			}

			stop:last-child {
				stop-color: ${token('color.background.brand.bold', '#2684FF')};
			}

			> g > g > path:last-child {
				fill: ${token('color.background.brand.bold', '#2684FF')};
			}
		}
	}
`;
